import { useState } from "react";
import { AiOutlinePlus } from "react-icons/ai";
import { GoUpload } from "react-icons/go";
import ActionPopover from "../../../../../components/popovers/actionPopover";
import useEditFormStore from "../../../../../state-management/useEditFormStore";
import {
	useManageTableWithSearchParams,
	useManageTableWithSearchParamsData,
} from "../../../../../helpers/useManageTable";
import TableFilter from "../../../../../components/table/filter/TableFilter";
import APISearchInput from "../../../../../components/search/APISearchInput";
import { PaymentInstitutionDto } from "../../../../../models/payment-institution.dto";
import DeleteModal from "../../../../../components/modals/DeleteModal";
import { PaymentInstitutionsColumns } from "../../../../admin/system_configurations/views/payment_Institutions/tanstack-columns";
import TanStackEmployeeTable from "../../../../../components/table/TanStackEmployeeTable";
import { TableId } from "../../../../../models/table-configuration.dto";
import useCompanyStore from "../../../../../state-management/useCompanyStore";
import {
	useDeleteCompanyPaymentInstitution,
	useGetCompanyPaymentInstitutionSearch,
	useCompanyGetPaymentInstitution,
	useUploadCompanyPaymentInstitutions,
} from "../../../../../services/company-payment-institution.service";
import {
	PermissionChecker,
	PERMISSIONS,
} from "../../../../../helpers/usePermissionManager";
import ENDPOINTS from "../../../../../services/utils/endpoints";
import CompanyPaymentInstitutionModalForm from "./modal";
import UploadPaymentInstitutionData from "./modal/UploadPaymentInstitutionData";

const CompanyPaymentInstitutions = () => {
	const { currentCompany } = useCompanyStore();
	const companyId = currentCompany?.id as number;

	const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
	const [openModal, setOpenModal] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);
	const [isEditForm, setIsEditForm] = useState(false);
	const [openUploadModal, setOpenUploadModal] = useState(false);

	const { mutate: searchPaymentInstitutions } =
		useGetCompanyPaymentInstitutionSearch();
	const { mutate: uploadPaymentInstitutions } =
		useUploadCompanyPaymentInstitutions();
	const {
		tableAPIRef,
		fetchedTableData,
		tableData,
		isLoading,
		pageCount,
		setTableData,
	} = useManageTableWithSearchParams<PaymentInstitutionDto>({
		fetchHook: (params) => useCompanyGetPaymentInstitution(companyId, params),
	});

	const handleClose = () => setAnchorEl(null);
	const handleOpenModal = () => {
		setAnchorEl(null);
		setIsEditForm(false);
		setOpenModal(true);
	};

	const { formDataValues, setFormData } = useEditFormStore();
	const [deleting, setDeleting] = useState(false);
	const { mutate: deletePaymentInstitution } =
		useDeleteCompanyPaymentInstitution();
	const { reloadTableData } = useManageTableWithSearchParamsData();

	const handleDeleteMethod = () => {
		setDeleting(true);
		deletePaymentInstitution(
			{ id: formDataValues?.id, companyId },
			{
				onSuccess: () => {
					reloadTableData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			},
		);
	};

	return (
		<div>
			<h2 className="text-2sm font-semibold mb-3">Payment Institutions</h2>
			<hr />
			<div className="flex justify-between items-center gap-6 mt-7 mb-8">
				<div className="flex items-center gap-3">
					<APISearchInput
						fallbackData={fetchedTableData}
						setTableData={setTableData}
						mutate={searchPaymentInstitutions}
					/>
					<TableFilter
						fields={[
							{
								title: "",
								fieldName: "",
								fieldType: "SORT",
								options: [
									{ title: "Name", fieldName: "name" },
									{ title: "Date Created", fieldName: "createdAt" },
								],
							},
							{
								title: "Active",
								fieldName: "active",
								fieldType: "YES_NO",
							},
						]}
					/>
				</div>
				<div className="flex items-center gap-6 shrink-0 whitespace-nowrap">
					<PermissionChecker permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}>
						<button
							type="button"
							onClick={() => setOpenUploadModal(true)}
							className="h-11 px-4 flex items-center gap-2 rounded-lg border border-gray-300 text-sm text-gray-700 hover:bg-gray-50"
						>
							<GoUpload className="text-base text-gray-500" />
							Upload
						</button>
					</PermissionChecker>
					<div
						className="flex items-center text-primary-blue text-sm cursor-pointer"
						onClick={handleOpenModal}
					>
						<AiOutlinePlus className="mr-1 text-sm" />
						Add New Payment Institution
					</div>
				</div>
			</div>
			<TanStackEmployeeTable
				tableId={TableId.PAYMENT_INSTITUTIONS}
				data={tableData}
				columns={PaymentInstitutionsColumns}
				loading={isLoading}
				onRowClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				onActionClick={(row, event) => {
					setFormData(row);
					setAnchorEl(event.currentTarget);
				}}
				initialColumnVisibility={{}}
				pageSize={10}
				pageCount={pageCount}
				apiRef={tableAPIRef}
			/>
			<ActionPopover
				id={anchorEl ? "simple-popover" : undefined}
				open={Boolean(anchorEl)}
				anchorEl={anchorEl}
				handleClose={handleClose}
				setOpenDeleteModal={setOpenDeleteModal}
				setOpenEdit={setIsEditForm}
				setOpenModal={setOpenModal}
			/>
			<DeleteModal
				openDeleteModal={openDeleteModal}
				spanContent={formDataValues?.name || ""}
				setOpenDeleteModal={setOpenDeleteModal}
				handleDeleteMethod={handleDeleteMethod}
				deleting={deleting}
			/>
			{openModal && (
				<CompanyPaymentInstitutionModalForm
					open={openModal}
					setOpen={setOpenModal}
					title={
						isEditForm
							? "Update Payment Institution"
							: "Add New Payment Institution"
					}
					isEditForm={isEditForm}
					tableAPIRef={tableAPIRef}
					companyId={companyId}
					reloadTableData={reloadTableData}
				/>
			)}
			{openUploadModal && (
				<UploadPaymentInstitutionData
					open={openUploadModal}
					setOpen={setOpenUploadModal}
					companyId={companyId}
					title="Upload Payment Institutions"
					recordLabel="payment institutions"
					templateUrl={
						ENDPOINTS.BILLING_AND_SUBSCRIPTION.PAYMENT_INSTITUTION
							.DONWLOAD_TEMPLATE
					}
					upload={uploadPaymentInstitutions}
				/>
			)}
		</div>
	);
};

export default CompanyPaymentInstitutions;
